import React, { useState, useEffect, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import { taskTypeService, connectionService, scheduleService, taskService } from '../services';
import {
  Checkbox,
  Select,
  FormTabs,
  formatSelectOptions,
  Input,
  TextareaAutosize,
} from '../components';
import { getBatchEditValue, TaskType, cronDescription } from '../helpers';
import { useGlobalState, useMessage } from '../hooks';

const _AddTaskForm = ({ mode, data, submitForm, loading, setLoading, formErrors = {} }, ref) => {
  const initialFormState = {
    task_id: null,
    task_name: '',
    task_description: '',
    task_type_id: null,
    task_priority: 100,
    connection_id: null,
    task_is_enabled: true,
    change_notification_email: '',
    schedule_ids: [],
  };

  const initialBatchEditItems = {
    task_priority: false,
    task_is_enabled: false,
    change_notification_email: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskType, setTaskType] = useState([]);
  const [connections, setConnections] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [currentConnection] = useGlobalState('currentConnection');
  const [settings, setSettings] = useGlobalState('settings');
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();

  const { showMessage, hideMessage } = useMessage();
  const location = useLocation();

  useEffect(() => {
    // Get form data
    fetchTaskTypes();
    fetchConnections();
    fetchSchedules();
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        task_id: data[0].task_id,
        task_name: data[0].task_name || '',
        task_description: data[0].task_description || '',
        task_type_id: data[0].task_type_id,
        task_priority: data[0].task_priority || '',
        connection_id: data[0].connection_id,
        task_is_enabled: data[0].task_is_enabled,
        change_notification_email: data[0].change_notification_email || '',
        schedule_ids: [],
      });
      fetchAssignedSchedules(data[0].task_id);
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        task_priority: getBatchEditValue(data, 'task_priority'),
        task_is_enabled: getBatchEditValue(data, 'task_is_enabled'),
        change_notification_email: getBatchEditValue(data, 'change_notification_email'),
      });
    } else {
      resetForm();
    }

    if (data.length > 0) {
      setTaskType(taskTypes.find(x => x.task_type_id === data[0].task_type_id));
    }
  }, [mode, data]);

  const fetchTaskTypes = async () => {
    taskTypeService
      .getAll()
      .then(res => {
        if (res && res.task_types) {
          const result = res.task_types.map(x => {
            let disabled = false;
            let tooltip = x.task_type_description;
            if (x.task_type_code === TaskType.adsync && !settings.OIDC_IS_ENABLED) {
              disabled = true;
              tooltip =
                'Enable Azure OpenID Connect Authentication to use this feature.\n' +
                x.task_type_description;
            }
            return {
              ...x,
              disabled: disabled,
              task_type_description: tooltip,
            };
          });
          setTaskTypes(result);

          if (data.length > 0) {
            setTaskType(res.task_types.find(x => x.task_type_id === data[0].task_type_id));
          }
          if (data.length === 0 && location.pathname.includes('/connections')) {
            const taskTypeTmp = res.task_types.find(x => x.task_type_code === 'dssync');
            setTaskType(taskTypeTmp);
            setFormData(prev => ({
              ...prev,
              task_type_id: taskTypeTmp.task_type_id,
              connection_id: currentConnection.connection_id,
            }));
          }
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchConnections = async () => {
    connectionService
      .getAll()
      .then(res => {
        setConnections(res.connections);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Fetches all schedules to populate the schedule multi-select.
   */
  const fetchSchedules = async () => {
    scheduleService
      .getAll()
      .then(res => {
        if (res?.schedules) {
          const enriched = res.schedules.map(schedule => {
            return {
              ...schedule,
              schedule_cron_description: cronDescription(schedule.schedule_cron),
            };
          });
          setSchedules(enriched);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Fetches the schedule IDs assigned to a task and pre-populates the schedule multi-select.
   * Called in edit mode after the rest of the form state has been set from the data prop.
   * @param {number} taskId
   */
  const fetchAssignedSchedules = async taskId => {
    taskService
      .getById(taskId)
      .then(res => {
        if (res?.tasks?.[0]?.schedule_ids) {
          setFormData(prev => ({
            ...prev,
            schedule_ids: res.tasks[0].schedule_ids.map(id => ({ value: id })),
          }));
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    if (name === 'task_type_id') {
      setTaskType(taskTypes.find(x => x.task_type_id === value));
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        if (name === 'schedule_ids') {
          // Multi-select: toggle { value } objects in/out of the array
          setFormData(prev => {
            const exists = prev.schedule_ids.find(x => x.value === value);
            return {
              ...prev,
              schedule_ids: exists
                ? prev.schedule_ids.filter(x => x.value !== value)
                : [...prev.schedule_ids, { value }],
            };
          });
        } else {
          setFormData({ ...formData, [name]: value });
        }
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          task_id: item.task_id,
          task_name: item.task_name,
          task_description: item.task_description,
          task_type_id: item.task_type_id,
          task_priority: item.task_priority,
          connection_id: item.connection_id,
          task_is_enabled: item.task_is_enabled,
          change_notification_email: item.change_notification_email,
          ...tmp,
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        connection_id:
          taskType.task_type_code === TaskType.dssync
            ? formData.connection_id
            : initialFormState['connection_id'],
        schedule_ids: formData.schedule_ids.map(s => s.value),
      };
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setTaskType([]);
    setDefaultSelectedTab({ tab: 'general' });
    setFormData({ ...initialFormState });
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="task_name"
              value={formData.task_name}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.task_name ? { valid: false, message: formErrors.task_name } : undefined
              }
            />
          </div>
        )}

        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            {!batchEdit && (
              <div className="form-block vertical">
                <TextareaAutosize
                  name="task_description"
                  value={formData.task_description}
                  onChange={handleInputChange}
                  disabled={loading}
                  isOptional={true}
                />
              </div>
            )}
            {!batchEdit && (
              <div className="form-block vertical">
                <Select
                  name="task_type_id"
                  value={formData.task_type_id}
                  options={formatSelectOptions({
                    options: taskTypes,
                    optionValue: 'task_type_id',
                    optionLabel: 'task_type_name',
                    tooltip: 'task_type_description',
                    optionDisabled: 'disabled',
                  })}
                  onChange={handleInputChange}
                  placeholder="Select task type..."
                  disabled={loading}
                  validation={
                    formErrors.task_type_id
                      ? { valid: false, message: formErrors.task_type_id }
                      : undefined
                  }
                />
              </div>
            )}

            {taskType && taskType.task_type_code === TaskType.dssync && (
              <React.Fragment>
                {!batchEdit && (
                  <div className="form-block vertical">
                    <Select
                      name="connection_id"
                      label="Connection"
                      value={formData.connection_id}
                      options={formatSelectOptions({
                        options: connections,
                        optionValue: 'connection_id',
                        optionLabel: 'connection_name',
                        optionSecondaryLabel: 'system_name',
                        optionIcon: 'connector_code',
                        tooltip: 'connection_description',
                      })}
                      onChange={handleInputChange}
                      placeholder="Select connection..."
                      disabled={loading}
                      validation={
                        formErrors.connection_id
                          ? { valid: false, message: formErrors.connection_id }
                          : undefined
                      }
                    />
                  </div>
                )}

                <div className="form-block vertical">
                  {batchEdit && (
                    <label>
                      <Checkbox
                        checked={batchEditItems.batch_change_notification_email}
                        name="batch_change_notification_email"
                        label="Edit Schema Change Notification Email"
                        onChange={e => handleInputChange(e, 'batch')}
                        disabled={loading}
                      />
                    </label>
                  )}
                  <TextareaAutosize
                    minRows={1}
                    name="change_notification_email"
                    label={!batchEdit && 'Schema Change Notification Email'}
                    title={
                      (settings.MAIL_IS_ENABLED
                        ? ''
                        : 'Enable and configure email notifications to use this feature.\n') +
                      'List of email addresses, separated by a comma, that will receive a notification email if schema changes are detected when running the task'
                    }
                    value={
                      formData.change_notification_email === 'multiple'
                        ? ''
                        : formData.change_notification_email
                    }
                    onChange={handleInputChange}
                    placeholder={
                      formData.change_notification_email === 'multiple' ? '< Multiple >' : ''
                    }
                    disabled={
                      loading ||
                      (batchEdit && !batchEditItems.batch_change_notification_email) ||
                      !settings.MAIL_IS_ENABLED
                    }
                    isOptional={true}
                  />
                </div>
              </React.Fragment>
            )}

            <div className="form-block vertical">
              {batchEdit ? (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_task_is_enabled}
                    name="batch_task_is_enabled"
                    label="Edit Status"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              ) : null}

              <Checkbox
                name="task_is_enabled"
                checked={formData.task_is_enabled}
                onChange={handleInputChange}
                label="Enabled"
                disabled={loading || (batchEdit && !batchEditItems.batch_task_is_enabled)}
              />
            </div>
          </div>
          <div label="Schedule">
            {/* schedule_ids is not batch-editable — each task has its own schedule assignments */}
            {!batchEdit && (
              <div className="form-block vertical">
                <Select
                  name="schedule_ids"
                  label="Schedules"
                  value={formData.schedule_ids}
                  title="Schedules this task is assigned to"
                  options={formatSelectOptions({
                    options: schedules,
                    optionValue: 'schedule_id',
                    optionLabel: 'schedule_name',
                    optionSecondaryLabel: 'schedule_cron_description',
                    tooltip: 'schedule_description',
                  })}
                  onChange={handleInputChange}
                  placeholder="Select schedules..."
                  disabled={loading}
                  isMulti={true}
                  isNullable={true}
                  isOptional={true}
                />
              </div>
            )}

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_task_priority}
                    name="batch_task_priority"
                    label="Edit Priority"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Input
                type="text"
                label={!batchEdit}
                name="task_priority"
                value={formData.task_priority === 'multiple' ? '' : formData.task_priority}
                onChange={handleInputChange}
                placeholder={formData.task_priority === 'multiple' ? '< Multiple >' : ''}
                disabled={loading || (batchEdit && !batchEditItems.batch_task_priority)}
                isOptional={true}
                validation={
                  formErrors.task_priority
                    ? { valid: false, message: formErrors.task_priority }
                    : undefined
                }
              />
            </div>
          </div>
        </FormTabs>
      </form>
    </div>
  );
};

export const AddTaskForm = forwardRef(_AddTaskForm);
